import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Avatar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Snackbar,
  LinearProgress,
  Switch
} from '@mui/material';
import {
  Dashboard,
  Grade,
  EventAvailable,
  Message,
  Person,
  School,
  TrendingUp,
  Event,
  Assignment,
  CheckCircle,
  Schedule,
  Folder,
  Send,
  Close,
  Description,
  PictureAsPdf,
  Warning,
  Download,
  Sms,
  Settings
} from '@mui/icons-material';
import ResourcesTab from '../components/ResourcesTab';
import apiClient from '../services/apiClient';

const ParentPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedChild, setSelectedChild] = useState('child1');
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [conferenceDialog, setConferenceDialog] = useState(false);
  const [conferenceRequest, setConferenceRequest] = useState({ teacher: '', preferredDate: '', reason: '' });
  const [messageText, setMessageText] = useState('');
  const [messageTo, setMessageTo] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [smsOptInAt, setSmsOptInAt] = useState(null);
  const [smsOptInIp, setSmsOptInIp] = useState(null);
  const [smsSaving, setSmsSaving] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('parentUser');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
  }, []);

  const mockChildren = [
    { id: 'child1', name: 'Emma Johnson', grade: '5th Grade', teacher: 'Mrs. Smith', id_num: 'STU-2024-0089', gpa: '3.6', attendance: '97.9%' },
    { id: 'child2', name: 'Alex Johnson', grade: '8th Grade', teacher: 'Mr. Davis', id_num: 'STU-2021-0034', gpa: '3.2', attendance: '93.1%' }
  ];

  const mockGrades = {
    child1: [
      { subject: 'Mathematics', teacher: 'Mrs. Smith', grade: 'A-', pct: 92, assignment: 'Chapter 3 Test', date: '2024-01-15' },
      { subject: 'English Language Arts', teacher: 'Mrs. Thompson', grade: 'B+', pct: 88, assignment: 'Book Report', date: '2024-01-12' },
      { subject: 'Science', teacher: 'Mr. Garcia', grade: 'A', pct: 96, assignment: 'Lab Experiment', date: '2024-01-10' },
      { subject: 'History', teacher: 'Mrs. Williams', grade: 'B+', pct: 89, assignment: 'WWII Project', date: '2024-01-08' },
      { subject: 'Bible Studies', teacher: 'Pastor Lee', grade: 'A', pct: 98, assignment: 'Scripture Memory', date: '2024-01-07' },
    ],
    child2: [
      { subject: 'Algebra I', teacher: 'Mr. Davis', grade: 'B', pct: 84, assignment: 'Quiz 5', date: '2024-01-14' },
      { subject: 'History', teacher: 'Mr. Carter', grade: 'A-', pct: 91, assignment: 'Essay Project', date: '2024-01-11' },
      { subject: 'Biology', teacher: 'Ms. Nguyen', grade: 'A', pct: 95, assignment: 'Cell Structure Test', date: '2024-01-09' },
      { subject: 'English', teacher: 'Mr. Brown', grade: 'B+', pct: 87, assignment: 'Poetry Analysis', date: '2024-01-08' },
    ]
  };

  const mockAttendance = {
    child1: { present: 142, absent: 3, tardy: 1, rate: '97.9%', records: [
      { date: 'Jan 15, 2024', status: 'Present' },
      { date: 'Jan 12, 2024', status: 'Present' },
      { date: 'Jan 11, 2024', status: 'Tardy', note: 'Doctor appointment' },
      { date: 'Jan 10, 2024', status: 'Present' },
      { date: 'Jan 09, 2024', status: 'Absent', note: 'Sick' },
    ]},
    child2: { present: 130, absent: 10, tardy: 3, rate: '93.1%', records: [
      { date: 'Jan 15, 2024', status: 'Present' },
      { date: 'Jan 12, 2024', status: 'Absent', note: 'Family event' },
      { date: 'Jan 11, 2024', status: 'Present' },
      { date: 'Jan 10, 2024', status: 'Tardy', note: 'Traffic' },
      { date: 'Jan 09, 2024', status: 'Present' },
    ]}
  };

  const mockDocuments = [
    { name: 'Q2 Report Card — 2023-24', type: 'pdf', date: 'Jan 12, 2024', category: 'Report Card' },
    { name: 'Science Fair Permission Slip', type: 'form', date: 'Jan 10, 2024', category: 'Permission Slip' },
    { name: 'Student Handbook 2023-24', type: 'pdf', date: 'Aug 20, 2023', category: 'Handbook' },
    { name: 'Medical Release Form', type: 'form', date: 'Aug 18, 2023', category: 'Medical' },
    { name: 'Field Trip — Natural History Museum', type: 'form', date: 'Nov 5, 2023', category: 'Permission Slip' },
    { name: 'Q1 Report Card — 2023-24', type: 'pdf', date: 'Oct 15, 2023', category: 'Report Card' },
  ];

  const mockMessages = [
    { from: 'Mrs. Smith (Math)', date: 'Jan 18, 2024', message: 'Emma did excellent work on the math project this week!', read: true },
    { from: 'Principal Johnson', date: 'Jan 15, 2024', message: 'Reminder: Parent-teacher conferences are scheduled for January 25–26. Sign-up links sent separately.', read: false },
    { from: 'Mr. Garcia (Science)', date: 'Jan 12, 2024', message: 'Please have Emma bring her lab notebook to class on Monday.', read: true },
  ];

  const currentChild = mockChildren.find(c => c.id === selectedChild);
  const currentGrades = mockGrades[selectedChild] || [];
  const currentAttendance = mockAttendance[selectedChild] || {};

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'success';
    if (grade.startsWith('B')) return 'warning';
    return 'error';
  };

  const getStatusColor = (status) => {
    if (status === 'Present') return 'success';
    if (status === 'Tardy') return 'warning';
    return 'error';
  };

  const handleSendMessage = () => {
    if (!messageText.trim() || !messageTo) return;
    setMessageText('');
    setSnackbar({ open: true, message: 'Message sent successfully' });
  };

  const handleRequestConference = () => {
    setConferenceDialog(false);
    setConferenceRequest({ teacher: '', preferredDate: '', reason: '' });
    setSnackbar({ open: true, message: 'Conference request submitted. You will receive a confirmation email.' });
  };

  const handleSmsSave = async () => {
    setSmsSaving(true);
    try {
      if (user?.id) {
        await apiClient.updateUser(user.id, { parentData: { sms_opt_in: smsOptIn } });
      }
      if (smsOptIn) setSmsOptInAt(new Date().toISOString());
      setSnackbar({ open: true, message: `SMS notifications ${smsOptIn ? 'enabled' : 'disabled'} successfully.` });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save SMS preference. Please try again.' });
    } finally {
      setSmsSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <School sx={{ fontSize: 60, color: 'primary.main', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>Parent Portal</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Sign in to view your child's grades, attendance, documents, and more.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate('/login')} sx={{ px: 4, py: 1.5 }}>
              Sign In
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 52, height: 52, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '1.4rem' }}>
                {user?.name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Welcome, {user?.name}!</Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>Parent / Guardian</Typography>
              </Box>
            </Box>
            <Button variant="outlined" onClick={() => { localStorage.removeItem('parentUser'); localStorage.removeItem('userRole'); setIsAuthenticated(false); navigate('/login'); }} sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Sign Out
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Child Selector */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 280 }}>
              <InputLabel>Select Child</InputLabel>
              <Select value={selectedChild} label="Select Child" onChange={(e) => setSelectedChild(e.target.value)}>
                {mockChildren.map(child => (
                  <MenuItem key={child.id} value={child.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>{child.name.charAt(0)}</Avatar>
                      <Box>
                        <Typography variant="body1">{child.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{child.grade} &bull; {child.teacher}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip icon={<TrendingUp />} label={`GPA: ${currentChild?.gpa}`} color="primary" variant="outlined" />
              <Chip icon={<EventAvailable />} label={`Attendance: ${currentChild?.attendance}`} color="success" variant="outlined" />
            </Box>
            <Button variant="outlined" startIcon={<Schedule />} onClick={() => setConferenceDialog(true)} sx={{ ml: 'auto' }}>
              Request Conference
            </Button>
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<Dashboard />} label="Dashboard" iconPosition="start" />
            <Tab icon={<Grade />} label="Grades" iconPosition="start" />
            <Tab icon={<EventAvailable />} label="Attendance" iconPosition="start" />
            <Tab icon={<Message />} label="Messages" iconPosition="start" />
            <Tab icon={<Folder />} label="Documents" iconPosition="start" />
            <Tab icon={<Description />} label="Resources" iconPosition="start" />
            <Tab icon={<Settings />} label="Settings" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Dashboard Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Quick Info */}
            <Grid item xs={12} md={5}>
              <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person color="primary" /> Student Information
                  </Typography>
                  {[
                    { label: 'Name', value: currentChild?.name },
                    { label: 'Grade', value: currentChild?.grade },
                    { label: 'Homeroom Teacher', value: currentChild?.teacher },
                    { label: 'Student ID', value: currentChild?.id_num },
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: i < 3 ? '1px solid' : 'none', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.value}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{currentChild?.gpa}</Typography>
                      <Typography variant="caption" color="text.secondary">Current GPA</Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'primary.50', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{currentChild?.attendance}</Typography>
                      <Typography variant="caption" color="text.secondary">Attendance</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activity & Events */}
            <Grid item xs={12} md={7}>
              <Card elevation={2} sx={{ borderRadius: 2, mb: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Activity</Typography>
                  <List dense disablePadding>
                    {[
                      { text: 'Math Chapter 3 Test graded — A-', icon: <Grade sx={{ fontSize: 16, color: 'success.main' }} /> },
                      { text: 'New assignment posted in English', icon: <Assignment sx={{ fontSize: 16, color: 'primary.main' }} /> },
                      { text: 'Science project submitted', icon: <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} /> },
                      { text: 'Parent-teacher conference email sent', icon: <Message sx={{ fontSize: 16, color: 'info.main' }} /> },
                    ].map((item, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Upcoming</Typography>
                  <List dense disablePadding>
                    {[
                      { event: 'Science Fair Registration', date: 'Feb 1' },
                      { event: 'Parent-Teacher Conference', date: 'Jan 25' },
                      { event: 'Field Trip — Natural History Museum', date: 'Feb 5' },
                      { event: 'Winter Break', date: 'Feb 15' },
                    ].map((item, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}><Event sx={{ fontSize: 16, color: 'warning.main' }} /></ListItemIcon>
                        <ListItemText primary={item.event} secondary={item.date} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Grades Tab */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>{currentChild?.name} — Recent Grades</Typography>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Teacher</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assignment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentGrades.map((g, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{g.subject}</TableCell>
                      <TableCell>{g.teacher}</TableCell>
                      <TableCell>{g.assignment}</TableCell>
                      <TableCell>
                        <Chip label={g.grade} color={getGradeColor(g.grade)} size="small" sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={g.pct} sx={{ flexGrow: 1, height: 6, borderRadius: 3 }} color={getGradeColor(g.grade)} />
                          <Typography variant="caption">{g.pct}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{g.date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Attendance Tab */}
        {activeTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Attendance Summary</Typography>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Days Present', value: currentAttendance.present, color: 'success' },
                      { label: 'Days Absent', value: currentAttendance.absent, color: 'error' },
                      { label: 'Tardies', value: currentAttendance.tardy, color: 'warning' },
                      { label: 'Attendance Rate', value: currentAttendance.rate, color: 'info' },
                    ].map((s, i) => (
                      <Grid item xs={6} key={i}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: `${s.color}.light`, borderRadius: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: `${s.color}.dark` }}>{s.value}</Typography>
                          <Typography variant="caption" color={`${s.color}.dark`}>{s.label}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Records</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(currentAttendance.records || []).map((r, i) => (
                          <TableRow key={i} hover>
                            <TableCell>{r.date}</TableCell>
                            <TableCell><Chip label={r.status} color={getStatusColor(r.status)} size="small" /></TableCell>
                            <TableCell><Typography variant="body2" color="text.secondary">{r.note || '—'}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {currentAttendance.absent >= 5 && (
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: 1 }} icon={<Warning />}>
                      Your child has accumulated {currentAttendance.absent} absences this term. Excessive absences may affect their academic progress.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Messages Tab */}
        {activeTab === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Messages</Typography>
                  {mockMessages.map((msg, i) => (
                    <Box key={i} sx={{ p: 2, mb: 1.5, borderRadius: 1, bgcolor: msg.read ? 'transparent' : 'primary.50', border: '1px solid', borderColor: msg.read ? 'divider' : 'primary.200' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: msg.read ? 500 : 700 }}>{msg.from}</Typography>
                        <Typography variant="caption" color="text.secondary">{msg.date}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">{msg.message}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Send Message</Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>To</InputLabel>
                    <Select value={messageTo} label="To" onChange={(e) => setMessageTo(e.target.value)}>
                      <MenuItem value="mrs-smith">Mrs. Smith (Homeroom / Math)</MenuItem>
                      <MenuItem value="principal">Principal Johnson</MenuItem>
                      <MenuItem value="counselor">School Counselor</MenuItem>
                      <MenuItem value="nurse">School Nurse</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Subject" fullWidth sx={{ mb: 2 }} size="small" />
                  <TextField label="Message" multiline rows={5} fullWidth sx={{ mb: 2 }} value={messageText} onChange={(e) => setMessageText(e.target.value)} />
                  <Button variant="contained" fullWidth startIcon={<Send />} onClick={handleSendMessage}>Send Message</Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Settings Tab */}
        {activeTab === 6 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Sms color="primary" /> SMS Notifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Receive text message alerts for grade updates, attendance issues, school announcements, and important reminders.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>Enable SMS Notifications</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {smsOptIn ? 'You are currently opted in' : 'You are currently opted out'}
                      </Typography>
                    </Box>
                    <Switch checked={smsOptIn} onChange={(e) => setSmsOptIn(e.target.checked)} color="primary" />
                  </Box>
                  {smsOptInAt && (
                    <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Opted in: {new Date(smsOptInAt).toLocaleString()}
                      </Typography>
                      {smsOptInIp && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          IP address recorded: {smsOptInIp}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Button variant="contained" onClick={handleSmsSave} disabled={smsSaving} fullWidth>
                    {smsSaving ? 'Saving…' : 'Save Preferences'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Documents Tab */}
        {activeTab === 4 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Documents &amp; Forms</Typography>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Document</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockDocuments.map((doc, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {doc.type === 'pdf' ? <PictureAsPdf color="error" /> : <Description color="primary" />}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{doc.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={doc.category} size="small" variant="outlined" /></TableCell>
                      <TableCell>{doc.date}</TableCell>
                      <TableCell>
                        <Button size="small" startIcon={<Download />} variant="outlined">Download</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Container>

      {/* Request Conference Dialog */}
      <Dialog open={conferenceDialog} onClose={() => setConferenceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Request Parent-Teacher Conference
          <IconButton onClick={() => setConferenceDialog(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Teacher</InputLabel>
              <Select value={conferenceRequest.teacher} label="Teacher" onChange={(e) => setConferenceRequest(p => ({ ...p, teacher: e.target.value }))}>
                <MenuItem value="mrs-smith">Mrs. Smith (Homeroom / Math)</MenuItem>
                <MenuItem value="principal">Principal Johnson</MenuItem>
                <MenuItem value="counselor">School Counselor</MenuItem>
              </Select>
            </FormControl>
            <TextField type="date" label="Preferred Date" InputLabelProps={{ shrink: true }} value={conferenceRequest.preferredDate} onChange={(e) => setConferenceRequest(p => ({ ...p, preferredDate: e.target.value }))} />
            <TextField label="Reason / Topics to Discuss" multiline rows={3} value={conferenceRequest.reason} onChange={(e) => setConferenceRequest(p => ({ ...p, reason: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setConferenceDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRequestConference}>Submit Request</Button>
        </DialogActions>
      </Dialog>

        {/* Resources Tab */}
        {activeTab === 5 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>School Resources</Typography>
            <ResourcesTab role="parent" />
          </Box>
        )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ParentPortal;
